import {
  Dispatch,
  SetStateAction,
  useMemo,
  useCallback,
  useState,
} from 'react';
import NoResultsOverlay from './NoResultsOverlay';
import { useMediaQuery } from '@mui/material';
import CustomToolbar from './CustomToolbar';
import {
  GridRowsProp,
  GridValidRowModel,
  GridRowIdGetter,
} from '@mui/x-data-grid';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import { Asset } from '@/types/asset_search';
import { AssetPanelContent } from './AssetPanelContent';

export type BaseTableProps<T extends GridValidRowModel = any> = {
  title: string;
  subTitle?: string;
  columns: (GridColDef | null)[];
  rows: GridRowsProp<T>;
  getRowId?: GridRowIdGetter<T>;
  handleRow?: any;
  disableVirtualization?: boolean;
  componentInsideTable?: any;
  search: string;
  isSuccess: boolean;
  isError: boolean;
  isFetchingAssets: boolean;
  handleShowDataTableMobile?: Dispatch<SetStateAction<boolean>>;
  showExport?: boolean;
};

const BaseTable = <T extends GridValidRowModel = any>({
  title,
  subTitle = '',
  columns,
  rows,
  getRowId,
  handleRow,
  isSuccess,
  isError,
  isFetchingAssets,
  disableVirtualization,
  componentInsideTable,
  search,
  handleShowDataTableMobile,
  showExport = false,
}: BaseTableProps<T>) => {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const columnsMemo = useMemo(
    () => columns.filter((x): x is GridColDef => x !== null),
    [columns],
  );
  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(
    ({ row }: { row: Asset }) => (
      <AssetPanelContent
        handleAssetSelected={handleRow}
        handleDataTableMobile={handleShowDataTableMobile}
        row={row as Asset}
      />
    ),
    [],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}
      className="datatable-container"
    >
      <DataGridPro
        showToolbar
        paginationMode="client"
        hideFooterPagination={false}
        disableColumnFilter
        disableColumnPinning={true}
        disableColumnSelector={!isDesktop}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={{ pageSize: pageSize, page: page }}
        pagination={rows.length > 10}
        getDetailPanelContent={!isDesktop ? getDetailPanelContent : undefined}
        getDetailPanelHeight={!isDesktop ? getDetailPanelHeight : undefined}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        rows={rows}
        columns={columnsMemo}
        getRowId={getRowId}
        onRowClick={({ row }) => {
          if (handleRow) {
            handleRow(row);
          }
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => {
            if (rows.length === 0 && !isFetchingAssets) {
              return (
                <NoResultsOverlay isSuccess={isSuccess} isError={isError} />
              );
            }
            return null;
          },
          noColumnsOverlay: () => {
            return <NoResultsOverlay isSuccess={isSuccess} isError={isError} />;
          },
        }}
        slotProps={{
          toolbar: {
            title,
            subTitle,
            search,
            showExport,
            items: rows,
            isFetching: isFetchingAssets,
          },
          root: {
            'data-testid': 'table-grid',
          } as any,
        }}
        disableVirtualization={disableVirtualization}
        getCellClassName={(params) => {
          if (
            params.field === 'asset_uuid' ||
            params.field === 'assetUuid' ||
            params.field === 'serialNumber'
          ) {
            return 'cell-link';
          }
          return 'cell';
        }}
        sx={(theme) => ({
          background: `${theme.palette.background.paper}!important`,
          flex: 1,
          height: '100%',
          '& .MuiDataGrid-virtualScroller': {
            flex: 1,
          },
          '& .custom-detail-column': {
            display: 'flex',
            justifyContent: 'flex-end',
          },
          '&.MuiDataGrid-root': {
            maxHeight: componentInsideTable ? '0px !important' : '100%',
          },
          '& .MuiDataGrid-main': {
            display: componentInsideTable ? 'none' : 'flex',
            flexDirection: 'column',
          },
          '& .MuiDataGrid-footerContainer': {
            display: componentInsideTable ? 'none' : 'block',
          },
          '& .MuiDataGrid-columnHeaders': {
            background:
              theme.palette.mode === 'light'
                ? theme.palette.primary.light
                : theme.palette.background.paper,
            border: 0,
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: '600' },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            background:
              theme.palette.mode === 'light'
                ? theme.palette.primary.light
                : theme.palette.background.paper,
          },
          '& .cell-link': {
            color: theme.palette.mode === 'light' ? '#0070BF' : '#80CAFF',
            cursor: 'pointer',
          },
          '& .MuiDataGrid-cell': {
            border: 0,
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        })}
      />
      {componentInsideTable}
    </div>
  );
};

export default BaseTable;
